import React, { useState, useEffect, useRef } from 'react';
import { 
  Send, MessageCircle, User as UserIcon, Trash2, Menu, X, ArrowLeft, 
  LayoutDashboard, ShoppingBag, History, TrendingUp, TrendingDown, 
  Users, Crown, Settings, LogOut, Image as ImageIcon, Wifi, WifiOff
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import socketService from '../../services/socketService';
import { adminService } from '../../services/adminService';
import { authService } from '../../services/authService';
import { chatService } from '../../services/chatService';
import API_CONFIG from '../../config/api';
import Toast from '../../components/Toast';
import { useTranslation } from '../../i18n/LocaleContext';

const AdminChat = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [connected, setConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState(new Set());
  const [toast, setToast] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [chatSidebarOpen, setChatSidebarOpen] = useState(true);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const previousScrollHeightRef = useRef(0);
  const selectedUserRef = useRef(null);
  const fileInputRef = useRef(null);

  // Check user type
  const userStr = localStorage.getItem('user');
  const currentUser = userStr ? JSON.parse(userStr) : null;
  const isReferralAdmin = currentUser?.userType === 'referral_admin';

  // Menu items for referral admin (only users and chat)
  const referralAdminMenuItems = [
    { path: '/admin/users', label: t('users'), icon: Users },
    { path: '/admin/chat', label: t('chatSupport'), icon: MessageCircle },
  ];

  // Full menu items for super admin
  const adminMenuItems = [
    { path: '/admin/dashboard', label: t('dashboard'), icon: LayoutDashboard },
    { path: '/admin/orders', label: t('orders'), icon: ShoppingBag },
    { path: '/admin/order-history', label: t('orderHistory'), icon: History },
    { path: '/admin/deposits', label: t('deposits'), icon: TrendingUp },
    { path: '/admin/withdrawals', label: t('withdrawals'), icon: TrendingDown },
    { path: '/admin/users', label: t('users'), icon: Users },
    { path: '/admin/vip', label: t('vip'), icon: Crown },
    { path: '/admin/chat', label: t('chatSupport'), icon: MessageCircle },
    { path: '/admin/settings', label: t('settings'), icon: Settings }
  ];

  // Choose menu based on userType
  const menuItems = isReferralAdmin ? referralAdminMenuItems : adminMenuItems;

  const handleLogout = () => {
    if (window.confirm(t('logoutConfirm'))) {
      authService.logout();
      window.location.href = '/'; // Force reload để App.js check lại auth
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    // Kết nối socket
    const socket = socketService.connect(token);

    const initializeChat = () => {
      setConnected(true);
      loadChatUsers(true);
    };

    socket.on('connect', initializeChat);
    if (socket.connected) initializeChat();

    socket.on('disconnect', () => {
      setConnected(false);
    });

    // Lắng nghe tin nhắn mới
    socketService.onNewMessage((message) => {
      // Cập nhật danh sách user
      loadChatUsers();

      // Nếu đang chat với user này, thêm tin nhắn vào
      const activeUser = selectedUserRef.current;
      if (activeUser && (message.sender_id === activeUser.id || message.receiver_id === activeUser.id)) {
        setMessages(prev => [...prev, {
          id: message.id,
          text: message.message,
          imageUrl: message.image_url,
          sender: message.sender_role === 'admin' ? 'admin' : 'user',
          timestamp: new Date(message.created_at),
          username: message.sender_username
        }]);
      }
    });

    // Lắng nghe typing
    socketService.onUserTyping((data) => {
      if (data.userId) {
        setTypingUsers(prev => new Set(prev).add(data.userId));
        setTimeout(() => {
          setTypingUsers(prev => {
            const newSet = new Set(prev);
            newSet.delete(data.userId);
            return newSet;
          });
        }, 3000);
      }
    });

    socketService.onUserStopTyping((data) => {
      if (data.userId) {
        setTypingUsers(prev => {
          const newSet = new Set(prev);
          newSet.delete(data.userId);
          return newSet;
        });
      }
    });

    return () => {
      socket.off('connect', initializeChat);
    };
  }, []);

  const loadChatUsers = (selectLatest = false) => {
    socketService.getChatUsers((response) => {
      if (response.success) {
        setUsers(response.users);
        if (selectLatest && !selectedUserRef.current && response.users.length > 0) {
          handleSelectUser(response.users[0]);
        }
      }
    });
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setMessages([]);
    setOffset(0);
    setHasMore(true);
    setChatSidebarOpen(false); // Đóng chat sidebar trên mobile

    try {
      // Lấy lịch sử chat với user này từ API (50 tin nhắn gần nhất)
      const response = await adminService.getChatHistory(user.id, 50, 0);
      if (response.success) {
        setMessages(response.messages.map(msg => ({
          id: msg.id,
          text: msg.message,
          imageUrl: msg.image_url,
          sender: msg.sender_role === 'admin' ? 'admin' : 'user',
          timestamp: new Date(msg.created_at),
          username: msg.sender_username || user.username
        })));
        setHasMore(response.hasMore);
        setOffset(50);
      }
    } catch (error) {
      console.error('Load chat history error:', error);
    }

    // Đánh dấu đã đọc
    socketService.markAsRead(user.id);
  };

  const loadMoreMessages = async () => {
    if (loading || !hasMore || !selectedUser) return;

    try {
      setLoading(true);
      const container = messagesContainerRef.current;
      previousScrollHeightRef.current = container.scrollHeight;

      const response = await adminService.getChatHistory(selectedUser.id, 50, offset);
      if (response.success && response.messages.length > 0) {
        const newMessages = response.messages.map(msg => ({
          id: msg.id,
          text: msg.message,
          imageUrl: msg.image_url,
          sender: msg.sender_role === 'admin' ? 'admin' : 'user',
          timestamp: new Date(msg.created_at),
          username: msg.sender_username || selectedUser.username
        }));
        
        setMessages(prev => [...newMessages, ...prev]);
        setHasMore(response.hasMore);
        setOffset(prev => prev + 50);

        // Giữ vị trí scroll
        setTimeout(() => {
          if (container) {
            container.scrollTop = container.scrollHeight - previousScrollHeightRef.current;
          }
        }, 0);
      }
    } catch (error) {
      console.error('Load more messages error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = () => {
    const container = messagesContainerRef.current;
    if (container && container.scrollTop === 0 && hasMore && !loading) {
      loadMoreMessages();
    }
  };

  const handleDeleteHistory = async () => {
    if (!selectedUser) return;
    
    if (!window.confirm(t('aChatConfirmDeleteHistory', { username: selectedUser.username }))) {
      return;
    }

    try {
      setDeleting(true);
      await adminService.deleteChatHistory(selectedUser.id);
      setMessages([]);
      setToast({ type: 'success', message: t('chatHistoryDeleted') });
      loadChatUsers(); // Reload danh sách user
    } catch (error) {
      setToast({ type: 'error', message: error.message || t('cannotDeleteHistory') });
    } finally {
      setDeleting(false);
    }
  };

  const handleSend = async () => {
    if ((!inputText.trim() && !selectedImage) || !connected || !selectedUser || uploadingImage) return;

    const messageText = inputText;
    const imageFile = selectedImage;
    setInputText('');
    setSelectedImage(null);
    setImagePreview(null);
    socketService.stopTyping(selectedUser.id);

    let imageUrl = null;
    if (imageFile) {
      try {
        setUploadingImage(true);
        const uploadRes = await chatService.uploadImage(imageFile);
        if (uploadRes.success) {
          imageUrl = uploadRes.imageUrl;
        }
      } catch (error) {
        console.error('Upload image error:', error);
        setUploadingImage(false);
        return;
      }
      setUploadingImage(false);
    }

    socketService.sendMessage(messageText, selectedUser.id, (response) => {
      if (!response.success) {
        console.error('Send message error:', response.error);
      }
    }, imageUrl);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      return;
    }

    setSelectedImage(file);
    setImagePreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleInputChange = (e) => {
    setInputText(e.target.value);
    
    if (selectedUser) {
      socketService.startTyping(selectedUser.id);
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      typingTimeoutRef.current = setTimeout(() => {
        socketService.stopTyping(selectedUser.id);
      }, 1000);
    }
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex h-screen w-screen bg-gray-900 relative overflow-hidden fixed inset-0">
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      
      {/* Overlay cho mobile khi mở menu */}
      {adminMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setAdminMenuOpen(false)}
        />
      )}
      
      {/* Admin Sidebar Menu - Luôn hiện desktop, toggle mobile */}
      <div className={`
        fixed lg:relative inset-y-0 left-0 z-50
        w-64 bg-gray-800
        border-r border-gray-700
        flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${adminMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-white">{t('adminPanel')}</h2>
            <button onClick={() => setAdminMenuOpen(false)} className="lg:hidden text-white">
              <X className="w-6 h-6" />
            </button>
          </div>

          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = item.path === '/admin/chat';
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setAdminMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-brand-500 text-white font-semibold' 
                      : 'text-gray-300 hover:bg-white hover:bg-opacity-10'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-300 hover:bg-red-500 hover:text-white transition-colors w-full mt-8"
          >
            <LogOut className="w-5 h-5" />
            <span>{t('logout')}</span>
          </button>
        </div>
      </div>
      
      {/* Overlay cho chat sidebar mobile */}
      {chatSidebarOpen && selectedUser && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setChatSidebarOpen(false)}
        />
      )}
      
      {/* Chat Sidebar - Danh sách user */}
      <div className={`
        fixed lg:relative inset-y-0 left-0 z-40
        w-full lg:w-80 xl:w-96
        bg-gray-800 border-r border-gray-700 
        flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${chatSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${!selectedUser ? 'translate-x-0' : ''}
      `}>
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAdminMenuOpen(true)}
                className="lg:hidden text-white hover:text-brand-400 transition-colors"
                title="Menu"
              >
                <Menu className="w-6 h-6" />
              </button>
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <MessageCircle className="w-6 h-6 text-brand-400" />
                <span className="hidden sm:inline">{t('customerMessages')}</span>
              </h2>
            </div>
          </div>
          <p className="text-xs text-gray-400 flex items-center gap-1.5">
            {connected ? (
              <><Wifi className="w-3.5 h-3.5 text-green-400" /> {t('connected')}</>
            ) : (
              <><WifiOff className="w-3.5 h-3.5 text-red-400" /> {t('disconnected')}</>
            )}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {users.length === 0 ? (
            <div className="p-4 text-center text-gray-400">
              <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{t('noMessages')}</p>
            </div>
          ) : (
            users.map((user) => (
              <div
                key={user.id}
                onClick={() => handleSelectUser(user)}
                className={`p-4 border-b border-gray-700 cursor-pointer transition-colors ${
                  selectedUser?.id === user.id
                    ? 'bg-gray-700'
                    : 'hover:bg-gray-700'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-brand-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-medium text-white truncate">{user.username}</p>
                      {user.unread_count > 0 && (
                        <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {user.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">{user.phone}</p>
                    {user.last_message && (
                      <p className="text-sm text-gray-300 truncate mt-1">
                        {user.last_message}
                      </p>
                    )}
                    {user.last_message_time && (
                      <p className="text-xs text-gray-500 mt-1">
                        {formatTime(user.last_message_time)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col">
        {selectedUser ? (
          <>
            {/* Header */}
            <div className="bg-gray-800 border-b border-gray-700 p-3 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  {/* Nút back trên mobile */}
                  <button
                    onClick={() => setChatSidebarOpen(true)}
                    className="lg:hidden text-white hover:text-brand-400 transition-colors flex-shrink-0"
                  >
                    <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
                  </button>
                  
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-brand-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-white text-sm sm:text-base truncate">{selectedUser.username}</h3>
                    <p className="text-xs text-gray-400 truncate">{selectedUser.phone}</p>
                  </div>
                </div>
                <button
                  onClick={handleDeleteHistory}
                  disabled={deleting || messages.length === 0}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-red-600 text-white rounded-lg text-xs sm:text-sm hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  title={t('aChatDeleteHistoryTitle')}
                >
                  <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">{deleting ? t('aChatDeleting') : t('deleteHistory')}</span>
                </button>
              </div>
            </div>

            {/* Messages */}
            <div 
              ref={messagesContainerRef}
              onScroll={handleScroll}
              className="flex-1 overflow-y-auto p-4 bg-gray-900"
            >
              {/* Loading indicator */}
              {loading && (
                <div className="text-center py-2">
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
                </div>
              )}
              
              {!hasMore && messages.length > 0 && (
                <div className="text-center py-2 text-gray-500 text-xs">
                  {t('allHistoryShown')}
                </div>
              )}
              
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`mb-3 sm:mb-4 flex ${message.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] sm:max-w-[75%] lg:max-w-[70%] rounded-2xl px-3 sm:px-4 py-2 sm:py-3 ${
                      message.sender === 'admin'
                        ? 'bg-brand-500 text-white'
                        : 'bg-gray-700 text-white'
                    }`}
                  >
                    {message.imageUrl && (
                      <img
                        src={API_CONFIG.getImageUrl(message.imageUrl)}
                        alt="chat"
                        className="max-w-full rounded-lg mb-1 cursor-pointer"
                        onClick={() => window.open(API_CONFIG.getImageUrl(message.imageUrl), '_blank')}
                      />
                    )}
                    {message.text && <p className="text-xs sm:text-sm whitespace-pre-line break-words">{message.text}</p>}
                    <p className={`text-xs mt-1 ${
                      message.sender === 'admin' ? 'text-white/70' : 'text-gray-300'
                    }`}>
                      {formatTime(message.timestamp)}
                    </p>
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {typingUsers.has(selectedUser.id) && (
                <div className="mb-4 flex justify-start">
                  <div className="bg-gray-700 rounded-2xl px-4 py-3">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="bg-gray-800 border-t border-gray-700 p-3 sm:p-4">
              {imagePreview && (
                <div className="mb-3">
                  <div className="relative inline-block">
                    <img src={imagePreview} alt="preview" className="h-20 rounded-lg border border-gray-600" />
                    <button
                      onClick={handleRemoveImage}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-gray-900 text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!connected || uploadingImage}
                  className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-700 text-gray-200 rounded-lg flex items-center justify-center hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                  <ImageIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
                <input
                  type="text"
                  value={inputText}
                  onChange={handleInputChange}
                  onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                  placeholder={connected ? t('messageInput') : t('connecting')}
                  disabled={!connected}
                  className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-gray-900 border border-gray-700 text-white text-sm sm:text-base rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-gray-800"
                />
                <button
                  onClick={handleSend}
                  disabled={!connected || (!inputText.trim() && !selectedImage) || uploadingImage}
                  className="px-4 sm:px-6 py-2 sm:py-3 bg-brand-500 text-white rounded-lg font-medium hover:bg-brand-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {uploadingImage ? (
                    <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                  )}
                  <span className="hidden sm:inline">{t('send')}</span>
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="hidden lg:flex flex-1 items-center justify-center bg-gray-900">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">{t('selectConversation')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminChat;
